import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ThunkConfig } from "../../types/responseType";
import { FormItem, FormListResponse } from "../../types/formType";
import FormApis from "../../api/formApis";

interface FormFilters {
  createdBy?: string;
  createdOn?: string;
  formName?: string;
}

interface FormState {
  loading: boolean;
  data: FormItem[];
  pagination: FormListResponse | null;
  filters: FormFilters;
  page: number;
  size: number;
}

const initialState: FormState = {
  loading: false,
  data: [],
  pagination: null,
  filters: {},
  page: 0,
  size: 10,
};

export const fetchForms = createAsyncThunk<
  FormListResponse,
  void,
  ThunkConfig
>("forms/fetchForms", async (_, thunkAPI) => {
  const state = thunkAPI.getState().forms;

  const response = await FormApis.getFormList({
    page: state.page,
    size: state.size,
    ...state.filters,
  });

  if (!response) return thunkAPI.rejectWithValue("Unable to fetch data");
  return response.data;
});

export const formSlice = createSlice({
  name: "forms",
  initialState,
  reducers: {
    setFormFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      state.page = 0; // reset page on filter
    },
    resetFormFilters: (state) => {
      state.filters = {};
      state.page = 0;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSize: (state, action) => {
      state.size = action.payload;
      state.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForms.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchForms.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.content;
        state.pagination = action.payload;
      })
      .addCase(fetchForms.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setFormFilters, resetFormFilters, setPage, setSize } =
  formSlice.actions;

export default formSlice.reducer;
